import { useState, useEffect, createRef, useRef } from "react";
import {
  Alert,
  Typography,
  Box,
  Grid,
  Stack,
  Button,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import Helper from "../../playcreator/functions/Helper.js";
import Phase from "../../playcreator/components/Phase.js";
import HoveringAnimationTimeline from "../../playcreator/components/HoveringAnimationTimeline.js";
import ViewerPlayToolbar from "./ViewerPlayToolbar.js";
import QuillDisplay from "../../components/QuillDisplay";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlay } from "@fortawesome/free-solid-svg-icons";
import AnimationDialog from "../../playcreator/components/AnimationDialog.js";
import PlayCreatorAd from "./PlayCreatorAd.js";
import FullScreenPhase from "../../playcreator/components/FullScreenPhase.js";

export default function Play(props) {
  const theme = useTheme();
  let play = props.play.error
    ? null
    : {
        ...props.play,
        data: Helper.convertToLatestDataModel(props.play),
        title: props.play.title ?? props.play?.data?.settings?.title,
      };

  let descriptionHTML = play?.data?.settings?.description;
  let phases = play?.data?.phases?.phases ?? Helper.createPlaceholder(4);
  const [showAnimationDialog, setShowAnimationDialog] = useState(false);
  const smallScreen = useMediaQuery(theme.breakpoints.down("md"));
  let isError = props.play.error;
  let showAds = false; // props.play.showAds;
  let videos = play?.data?.settings?.videos ?? [];
  const showAnimationButton = Helper.hasAnimation(play);
  const [mirror, setMirror] = useState(false);

  const fullScreenPhaseRef = useRef();

  return (
    <Box sx={{ fontSize: 16, px: "0px !important", boxSizing: "border-box" }}>
      {play && (
        <FullScreenPhase
          phases={phases}
          mirror={mirror}
          setMirror={setMirror}
          settings={play.data.settings}
          ref={fullScreenPhaseRef}
        />
      )}

      {showAds && (
        <div
          style={{
            marginBottom: 20,
          }}
          className="adthrive-leaderboard"
        />
      )}
      {!isError && <ViewerPlayToolbar code={play.code} play={play} />}
      {isError && (
        <Alert sx={{ mt: 4 }} severity="error">
          {props.play.error}
        </Alert>
      )}
      {play && showAnimationDialog && (
        <AnimationDialog
          play={play}
          open={showAnimationDialog}
          close={() => setShowAnimationDialog(false)}
        />
      )}
      <Stack
        spacing={smallScreen ? 2 : 4}
        direction={{ xs: "column", md: "row" }}
        sx={{
          mt: 5,
          mb: smallScreen ? 4 : 2,
          alignItems: { xs: "flex-start", md: "center" },
          justifyContent: { xs: "flex-start", md: "space-between" },
        }}
      >
        <Typography variant="h3">
          {play?.title === "" ? "Untitled Play" : play?.title}
        </Typography>
        {play && showAnimationButton && (
          <Box sx={{ flexShrink: 0 }}>
            <Button
              sx={{ boxShadow: { xs: "none", md: "initial" } }}
              size={smallScreen ? "small" : "regular"}
              color="primary"
              variant="contained"
              startIcon={<FontAwesomeIcon icon={faPlay} />}
              onClick={() => {
                setShowAnimationDialog(true);
              }}
            >
              Play Animation
            </Button>
          </Box>
        )}
      </Stack>
      <Box sx={{ mb: 3, maxWidth: 800 }}>
        <QuillDisplay content={descriptionHTML} />
      </Box>
      {showAds && (
        <div
          style={{
            marginBottom: 20,
          }}
          className="adthrive-ad"
        />
      )}
      <Grid spacing={4} container>
        {!isError &&
          phases.map((phase, index) => {
            let notesHTML = phase.note;
            let notesText = Helper.stripHtml(notesHTML);
            return (
              <Grid xs={12} sm={6} lg={4} item key={phase.id}>
                <Box sx={{ mb: 1, fontSize: 14, fontWeight: 600 }}>
                  {Helper.getPhaseTitle(phase, phases, "output")}
                </Box>

                <Box
                  sx={{
                    mb: 1,
                    position: "relative",
                    "&:hover .timeline_box": {
                      opacity: 1,
                      visibility: "visible",
                    },
                  }}
                >
                  <Phase
                    objectSizeFactor={play.data.settings.objectSizeFactor}
                    uId={"viewer_" + phase.id}
                    courtColor={play.data.settings.courtColor}
                    id={phase.id}
                    editable={false}
                    animatable={true}
                    court={play.data.settings.editor.court}
                    dispatch={() => {}}
                    phase={phase}
                    mirror={mirror}
                  />

                  <HoveringAnimationTimeline
                    requestFullscreen={() => {
                      fullScreenPhaseRef.current.setCurrentPhaseIndex(index);
                      fullScreenPhaseRef.current.requestFullscreen();
                    }}
                    mirror={mirror}
                    setMirror={setMirror}
                    updateSteps={() => {}}
                    prefix={"viewer_" + phase.id}
                    phase={phase}
                    editor={play.data.settings.editor}
                  />
                </Box>

                {notesText.trim().length > 0 && (
                  <QuillDisplay content={notesHTML} />
                )}
                {showAds && index !== phases.length - 1 && (
                  <div className="adthrive-ad-desktop-hidden"></div>
                )}
              </Grid>
            );
          })}
      </Grid>
      {showAds && (
        <div
          style={{
            marginBottom: 20,
          }}
          className="adthrive-ad"
        />
      )}
      {videos.length > 0 && (
        <Box sx={{ mt: 8 }}>
          <Grid spacing={4} container>
            {videos.map((video, index) => {
              let youtubeID =
                video.source === "youtube" &&
                Helper.extractYoutubeIdFromUrl(video.url);
              return (
                <Grid xs={12} sm={6} xl={6} item key={index}>
                  <div
                    style={{
                      marginBottom: 16,
                      position: "relative",
                      paddingTop: "56.25%",
                    }}
                  >
                    {video.source === "library" && (
                      <iframe
                        title="video"
                        src={
                          "https://iframe.mediadelivery.net/embed/" +
                          video?.library_id +
                          "/" +
                          video?.guid +
                          "?autoplay=false&preload=true"
                        }
                        loading="lazy"
                        style={{
                          border: "none",
                          position: "absolute",
                          top: 0,
                          height: "100%",
                          width: "100%",
                        }}
                        allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture;"
                        allowfullscreen="true"
                      />
                    )}

                    {video.source === "youtube" && youtubeID && (
                      <iframe
                        style={{
                          border: "none",
                          position: "absolute",
                          top: 0,
                          height: "100%",
                          width: "100%",
                        }}
                        src={"https://www.youtube.com/embed/" + youtubeID}
                        title="YouTube video player"
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      />
                    )}

                    {video.source === "youtube" && !youtubeID && (
                      <Alert severity="error">
                        Couldn't load Youtube Video
                      </Alert>
                    )}
                  </div>
                  <QuillDisplay content={video.note} />
                </Grid>
              );
            })}
          </Grid>
        </Box>
      )}
      <Box
        sx={{
          backgroundColor: "#fafafa",
          mt: 15,
          width: "100vw",
          position: "relative",
          left: "50%",
          right: "50%",
          marginLeft: "-50vw",
          marginRight: "-50vw",
          textAlign: "center",
          pb: 2,
          borderTop: "1px solid #eeeeee",
        }}
      >
        <Box
          sx={{
            margin: "0 auto",
            maxWidth: 1200,
            padding: 0,
          }}
        >
          <PlayCreatorAd
            queryParams={{
              utm_source: "blog",
              utm_medium: "viewer",
              utm_campaign: "footer",
              utm_term: play?.user_id,
            }}
          />
        </Box>
      </Box>
    </Box>
  );
}
